import { Component, inject, OnDestroy } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ActiveModal } from '@rocketfinancialcorp/rocket-ui/modal';
import { RktButtonDirective } from '@rocketfinancialcorp/rocket-ui/button';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';
import {
  formDatepicker,
  formInput,
  formCheckboxGroup,
  formRow,
  formTextarea,
  formTimepicker,
  RktFormModule,
} from '@rocketfinancialcorp/rocket-ui/form';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { FundsOnHoldService } from '@shared/services';
import { ErrorUtils } from '@shared/utils';
import { FormModel, FundOnHold } from '@shared/models';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-update-fund-on-hold',
  templateUrl: './update-fund-on-hold.component.html',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RktFormModule, RktButtonDirective, RktIconComponent],
})
export class UpdateFundOnHoldComponent implements OnDestroy {
  activeModal = inject(ActiveModal);
  private fundsOnHoldService = inject(FundsOnHoldService);

  fundOnHoldId = '';

  updateForm = new FormGroup({});

  isLoading = false;

  formError = '';

  updateFields = [
    formRow([
      formDatepicker({
        key: 'expiryDate',
        label: 'Expiry Date',
        props: {
          placeholder: 'MM/DD/YY',
          minDate: NgbDate.from({ year: new Date().getFullYear(), month: new Date().getMonth() + 1, day: new Date().getDate() }),
          maxDate: NgbDate.from({ year: 2099, month: 12, day: 31 }),
        },
      }),
      formTimepicker({
        key: 'expiryTime',
        label: 'Expiry Time',
      }),
    ]),
    formRow([
      formTextarea({
        key: 'memo',
        label: 'Memo',
        props: {
          maxLength: 500,
          rows: 3,
        },
      }),
    ]),
    formRow([
      formInput({
        key: 'externalReferenceId',
        label: 'External Reference ID',
      }),
    ]),
    formRow([
      formCheckboxGroup({
        key: 'release',
        label: ' ',
        props: {
          options: [{ value: 'RELEASED', label: 'Release this hold' }],
        },
      }),
    ]),
  ];

  formModel: FormModel<{
    expiryDate: string;
    expiryTime: string;
    memo: string;
    externalReferenceId: string;
    release: Record<string, boolean>;
  }> = {};

  private destroy$ = new Subject<void>();

  private toISODate(dateStr: string): string {
    if (dateStr.includes('/')) {
      const [month, day, year] = dateStr.split('/');
      const fullYear = year.length === 2 ? `20${year}` : year;
      return `${fullYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateStr;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  modalInitData(fundOnHold: FundOnHold): void {
    this.fundOnHoldId = fundOnHold.id;

    // Split expiry into date and time parts if it contains time info
    let expiryDate = fundOnHold.expiry;
    let expiryTime = '';

    if (fundOnHold.expiry && fundOnHold.expiry.includes('T')) {
      const parts = fundOnHold.expiry.split('T');
      expiryDate = parts[0];
      expiryTime = parts[1]?.substring(0, 8) || '';
    }

    this.formModel = {
      expiryDate,
      expiryTime,
      memo: fundOnHold.memo,
      externalReferenceId: fundOnHold.externalReferenceId,
      release: {},
    };
  }

  onSubmit(): void {
    this.updateForm.markAllAsTouched();

    if (this.updateForm.invalid) {
      return;
    }

    this.isLoading = true;
    this.formError = '';

    const { expiryDate, expiryTime, memo, externalReferenceId, release } = this.formModel;

    // Convert date to ISO 8601 format
    let expiry: string | undefined;
    if (expiryDate) {
      const isoDate = this.toISODate(expiryDate);
      expiry = expiryTime ? `${isoDate}T${expiryTime}Z` : `${isoDate}T00:00:00Z`;

      if (new Date(expiry) <= new Date()) {
        this.isLoading = false;
        this.formError = 'Expiry must be a future date and time.';
        return;
      }
    }

    this.fundsOnHoldService
      .updateFundOnHold(this.fundOnHoldId, {
        ...(expiry !== undefined ? { expiry } : {}),
        ...(memo !== undefined ? { memo } : {}),
        ...(externalReferenceId !== undefined ? { externalReferenceId } : {}),
        ...(release?.['RELEASED'] ? { status: 'RELEASED' } : {}),
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          this.activeModal.close(response);
        },
        error: (error) => {
          this.isLoading = false;
          this.formError = ErrorUtils.parseExtendedError(error);
        },
      });
  }
}
